import { lazy, Suspense, type PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import type { Ruolo } from '@/types';
import { homePath, paths } from './paths';

const LandingPage = lazy(() => import('@/pages/landing/LandingPage'));

function FullPageLoader(): JSX.Element {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Verifica della sessione…
      </Typography>
    </Box>
  );
}

export interface ProtectedRouteProps extends PropsWithChildren {
  /** Roles allowed through. Omit to require only authentication. */
  roles?: readonly Ruolo[];
}

/**
 * Route guard.
 *
 * Three distinct outcomes, in order:
 *  1. session still being verified → loader (never redirect on `idle`, or a
 *     page refresh would bounce an authenticated user to the login screen);
 *  2. not authenticated on `/` → public marketing home;
 *     any other app path → `/login`, remembering the target in location state
 *     so the user lands where they intended after signing in;
 *  3. authenticated but wrong role → `/403`.
 *
 * This is navigation ergonomics only. Authorisation is enforced by the API and
 * by RLS; hiding a route does not protect the data behind it.
 */
export function ProtectedRoute({ roles, children }: ProtectedRouteProps): JSX.Element {
  const status = useAuthStore((state) => state.status);
  const role = useAuthStore((state) => state.user?.ruolo ?? null);
  const location = useLocation();

  if (status === 'idle' || status === 'loading') return <FullPageLoader />;

  if (status !== 'authenticated') {
    if (location.pathname === paths.home) {
      return (
        <Suspense fallback={<Box aria-hidden sx={{ minHeight: '100vh', bgcolor: '#0A1F44' }} />}>
          <LandingPage />
        </Suspense>
      );
    }
    return <Navigate to={paths.login} state={{ from: location }} replace />;
  }

  if (roles && (!role || !roles.includes(role))) {
    return <Navigate to={paths.forbidden} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

/**
 * Role gate. Must sit inside `ProtectedRoute` (or check auth itself via the
 * shared loader). Unauthorized roles land on `/403` instead of a silent
 * redirect to the dashboard — the user needs to know why the URL failed.
 */
export function RoleRoute({ roles, children }: Required<Pick<ProtectedRouteProps, 'roles'>> & PropsWithChildren): JSX.Element {
  return <ProtectedRoute roles={roles}>{children}</ProtectedRoute>;
}

/** Keeps authenticated users away from the login page. */
export function PublicOnlyRoute({ children }: PropsWithChildren): JSX.Element {
  const status = useAuthStore((state) => state.status);
  const ruolo = useAuthStore((state) => state.user?.ruolo ?? null);

  if (status === 'idle' || status === 'loading') return <FullPageLoader />;
  if (status === 'authenticated') return <Navigate to={homePath(ruolo)} replace />;

  return <>{children ?? <Outlet />}</>;
}
